import gql from 'graphql-tag';
import { OFFER_FRAGMENT } from 'core/fragments/offer';
import {
  FARM_LOCATIONS_FRAGMENT,
  FARM_LIABILITY_COVERAGE_FRAGMENT,
  FARM_VERIFICATION_FRAGMENT
} from 'core/fragments/farm';

export const Address = gql`
  fragment Address on AddressDetails {
    address
    address2
    unit
    country
    city
    state
    zip
  }
`;

export const People = gql`
  fragment People on PersonDetails {
    id
    age
    isPrimary
    isCoApplicant
    firstName
    middleName
    lastName
    suffix
    gender
    excludeDriver
    maritalStatus
    takenCourse
    courseDiscountConditionCheck
    highestEducation
    dateOfBirth
    driversLicenseNumber
    driversLicenseState
    skipLicenseNumValidation
    ageFirstLicensed
    relationshipToInsured
    fullTimeStudent
    goodStudent
    exclusionReason
    removedReason
    removedBy
    removedTimestamp
    schoolName
    addDate
    communityDriveParticipation
    communityDriveScore
    everCommunityDriveParticipation
    needSR22
    lapsedLicense
    hasViolationsBlockingUmbrella
    insuranceScore {
      homeTotal
      autoTotal
      total
    }
    schoolLocation {
      ...Address
    }
    autoViolationDates
    waivedPIPWL
    pipEligible
    pipOptOut
    pipHasQHC
    assignedCar
    userAssignedCar
    autoViolations {
      AAF
      AFM
      ANC
      ANO
      ASW
      BOT
      CCW
      CML
      CMP
      CMPTV
      CMU
      CRD
      DEQ
      DEV
      DR
      DWI
      FAR
      FDL
      FEL
      FFR
      FLE
      FRA
      FTC
      FTY
      HOM
      IBK
      IP
      IT
      LDL
      LIC
      LTS
      MAJ
      MMV
      NAF
      NFX
      PUA
      PUN
      REF
      RKD
      SAF
      SCH
      SLV
      SPD
      SUS
      TMP
      UDR
      WSR
    }
  }
`;

export const PolicyFragment = gql`
  fragment PolicyFragment on Policy {
    id
    offerId
    policyType
    paymentType
    paymentMode
    directBillPreferredPaymentMethod
    isBix
    isAgentSold
    source
    canEndorse
    wasRenewedFromImportedTerm
    lastInspectionDate
    importedData {
      conversionMetadata {
        policyNumber
        carrier
        sourceComments
      }
    }
    homeInspectionRepairsRequested
    salesRep
    branchPlusLinkedOfferId
    bindRep
    paymentMethod
    premiumBreakdown
    webUserId
    policyDetails {
      monthsAtCurrentAddress
      autoCoverage {
        policyLimitBIPD
        policyLimitUMBI
        policyLimitUIMBI
        policyLimitPIP
        policyLimitUMPD
        policyLimitMedicalPayments
        policyLimitLPD
        petInjury
        coverageEducatorAdvantage
        coverageOffPremisesExpanded
        policyLimitPIPME
        policyLimitPIPWL
        policyLimitPIPACR
        policyLimitPPI
        policyLimitIncomeLoss
        policyLimitAccidentalDeath
        policyLimitFuneralBenefits
        policyLimitExtraMedBenefits
        policyLimitTortOption
        policyLimitComboFBP
        policyLimitGuestPIP
        policyLimitNoFaultPIP
        uimAddedOn
        skipPDValidation
      }
      branchPlusPolicies {
        policyType
        policyCarrier
        policyNumber
        attestationAgent
      }
      homeCoverage {
        coverageA
        deductibleAllOther
        deductibleWindHail
        deductibleHurricane
        coverageCPctOfA
        coverageBPctOfA
        coverageX
        coverageJ
        coverageWaterBackup
        coverageG
        interestedInSeparateWindHail
        externalPolicyId
      }
      drivers {
        ...People
      }
      nonDrivers {
        ...People
      }
      people {
        ...People
      }
      removedDrivers {
        ...People
      }
      cars {
        addDate
        VIN
        year
        make
        model
        bodyType
        primaryUse
        purchaseDate
        brandedTitle
        allowBrandedTitleCoverage
        allowBrandedTitleCompColl
        garageLocation {
          ...Address
        }
        isGarageAddressRuleExceptionApproved
        deductibleCollision
        deductibleComprehensive
        limitUMPD
        limitRental
        limitACPE
        limitIncomeLoss
        roadsideAssistance
        extendedNonOwnedCoverage
        solelyOwnedByApplicant
        jointOwner
        rideSharing
        umuimRejected
        confirmed
        aebStatus
        aebConfirmed
        antiTheftStatus
        antiTheftConfirmed
        singleAirbagStatus
        singleAirbagConfirmed
        doubleAirbagStatus
        doubleAirbagConfirmed
        luxury
        coverageLoan
        symbolMake
        symbolModel
        symbolStyle
        symbolAux
        annualMilesDriven
        hasOutstandingRecall
        symbolPGS
        symbolBI
        symbolPD
        symbolMed
        symbolPIP
        symbolColl
        symbolComp
        symbolUM
      }
      trailers {
        VIN
        type
        primaryUse
        year
        value
        contents
        purchaseDate
        garageLocation {
          ...Address
        }
        deductibleCollision
        deductibleComprehensive
        confirmed
        coverageLoan
        addDate
      }
      includeUmbrella
      includeRenters
      includeEarthquake
      includeFlood
      earthquakeCoverage {
        policyId
      }
      floodCoverage {
        policyId
      }
      attachedHomeowners
      attachedRenters
      attachedAuto
      attachedCondo
    }
    overrides {
          canEditUneditablePropertyDetails
          allowExcludingDrivers
          overridesForFeFormValidation {
            skipBogusLoanNumberCheck
            skipUmbrellaBIPDValidation
          }
          overridesForUW {
            code
            overridingAgent
            type
          }
          rc1 {
            itemsChanged
          }
        }
    billingDayOfMonth
    billingHoldUntil
    blockCreditCheckAtRenewal
    rateControlDate
    effectiveDate
    endDate
    fullTermPolicyEndDate
    inceptionDate
    autoRenew
    sendPaperDocuments
    renewalCreditPull
    renewalPaymentType
    renewalPaymentMethod
    renewalVariables {
      allow300KCSL
      allowForcedNonRenewal
      allowUmbrella
    }
    state
    term
    version
    premium
    surplusContribution
    rewriteReason
    cancelReason
    nonRenewReason
    cancelled
    nonRenewed
    fullTermCancel
    stripeCustomerId
    otherStripeCustomers {
      stripeId
      active
      paymentMethod
    }
    defaultCreditCard {
      id
      brand
      last4
      bankName
    }
    defaultBankAccount {
      id
      routingNumber
      accountHolder
      accountType
    }
    defaultEscrowAccount {
      mortgageHolderName
      loanNumber
      mortgageHolderAddress {
        address
        address2
        country
        state
        city
        zip
      }
      mortgageHolderEmail
      mortgageHolderFax
    }
    accountDetails {
      accountHolder
      accountType
      routingNumber
      accountNumber
    }
    bankAccounts {
      accountHolder
      accountType
      routingNumber
      accountNumber
    }
    creditCards {
      id
      brand
      last4
    }
    additionalParties {
      id
      name
      email
      relationship
      lossPayee
      associatedEntities
      address {
        address
        address2
        unit
        country
        city
        state
        zip
      }
      loanNumber
      VIN
    }
    farmInterestedParties {
      id
      name
      email
      relationship
      description
      isResident
      entityType
      fo41 {
        description
        locations
        risks
        equipment
        sppItems
        rpsItems
      }
      associatedEntities {
        entityType
        locationId
        riskId
        itemId
      }
      address {
        address
        address2
        unit
        country
        city
        state
        zip
      }
    }
    fees {
      type
      description
      amount
      appliedDate
    }
    fromStaff
    offer {
      ...OfferFragment
    }
    versionHistory {
      version
      username
      updatedDateTime
      coverageChange
      itemsChanged
      itemsChangedAddendum
      effectiveDate
      internalNotes
      changeInPremium
      totalPrice
    }
    segments {
      segmentId
      clarionDoorId
      farmClarionDoorIds {
        liabilityClarionDoorId
        risksClarionDoorIds {
          riskId
          clarionDoorId
        }
      }
      startDate
      dailyRate
      segmentDays
      segmentPremium
      entity {
        id
        type
        name
        address {
          address
          address2
          city
          state
          zip
        }
      }
      farmLocations {
        ...FarmLocationsFragment
      }
      includeFarmLiability
      farmLiabilityCoverage {
        ...FarmLiabilityCoverageFragment
      }
      farmCoverage {
        identityFraudLimit
        extraExpenseLimit
        propertyInTransitLimit
        hasEquipmentBreakdownCoverage
        propertyInCareIncreasedLimit
        fungiPropertyLimit
        fungiLiabilityLimit
        pollutantCleanupLimit
        pollutantCleanupDeductible
        machineryNotDescribedLimit
        newlyAcquiredMachineryLimit
        rentedOrBorrowedEquipmentLimit
        replacementMachineryLimit
        policyLevelAdjustmentFactor
        adjustmentRationale
      }
      autoCoverage {
        policyLimitBIPD
        policyLimitUMBI
        policyLimitUIMBI
        policyLimitPIP
        policyLimitUMPD
        policyLimitMedicalPayments
        policyLimitLPD
        petInjury
        coverageEducatorAdvantage
        coverageOffPremisesExpanded
        policyLimitPIPME
        policyLimitPIPWL
        policyLimitPIPACR
        policyLimitPPI
        policyLimitIncomeLoss
        policyLimitAccidentalDeath
        policyLimitFuneralBenefits
        policyLimitExtraMedBenefits
        policyLimitTortOption
        policyLimitComboFBP
        policyLimitGuestPIP
        policyLimitNoFaultPIP
        uimAddedOn
        skipPDValidation
      }
      scheduledPersonalProperty {
        deductible
        items {
          category
          description
          value
          breakageOption
          exTheftOption
        }
      }
      premium
      includeUmbrella
      includeRenters
      includeEarthquake
      includeFlood
      earthquakeCoverage {
        policyId
      }
      floodCoverage {
        policyId
      }
      attachedHomeowners
      attachedRenters
      attachedAuto
      attachedCondo
      umbrellaCoverage {
        highRisk
        numVehiclesTotal
        numLicensedDriversUnder25
        numMotorcyclesScooters
        numATVs
        numPersonalWatercraft
        numWatercraft
        watercraftHullLengths
        numRVs
        numRentalProperties
        uninsuredMotoristLimit
        rentalPropertyAddresses {
          ...Address
        }
        numOtherProperties
        otherPropertyAddresses {
          ...Address
        }
        presenceOfBusiness
        liabilityCoverageLimit
        limitBIOccurrence
      }
      branchPlusPolicies {
        policyType
        policyCarrier
        policyNumber
        attestationAgent
      }
      homeCoverage {
        coverageA
        coverageIBSC
        coverageBCPctOfA
        coverageBPctOfA
        coverageCPctOfA
        coverageDPctOfA
        coverageX
        coverageY
        coverageWaterBackup
        coverageBuildingMaterialsTheft
        coverageCourseOfConstruction
        coverageExtendedPremises
        coverageEducatorAdvantage
        coverageOffPremisesExpanded
        coverageFairRental
        coveragePetBite
        coverageRSExtended
        coverageYardAndGarden
        coverageSinkhole
        coverageOilStorageTank
        coverageBP
        coverageCA
        coverageDC
        coverageDR
        coverageF
        coverageG
        coverageJ
        coverageMI
        coverageMS
        coverageSP
        coverageST
        coverageEBSL
        coverageFortifiedRoof
        homeownerProtection
        deductibleAllOther
        deductibleHurricane
        deductibleWaterBackup
        deductibleWindHail
        windHailExclusion
        minimumDeductibleValue
        minimumWindHailDeductible
        minimumWindHailDeductibleValue
        minimumHurricaneDeductible
        minimumHurricaneDeductibleValue
        interestedInSeparateWindHail
        externalPolicyId
      }
      condoCoverage {
        coverageC
        coverageA
        coverageBCPctOfC
        coverageDPctOfC
        coverageEBSL
        coverageX
        coverageY
        coverageF
        coverageJ
        coverageBP
        coverageMI
        coverageCA
        coverageSP
        coverageST
        coverageDR
        coveragePetBite
        coverageIBSC
        minimumDeductibleValue
        personalPropReimbursement
        coverageG
        coverageWaterBackup
        coverageMSALE
        coverageMS
        deductibleAllOther
      }
      global {
        affinity
        affinityGroupStem
        rateControlDate
        currentlyHomeInsured
        currentAutoAMBEST
        currentAutoCarrier
        currentAutoCarrierStartDate
        currentAutoCarrierEndDate
        currentAutoCarrierPolicyNumber
        continuousAutoCoverageStartDate
        mostRecentAutoInsuranceCoverageEndDate
        manualInventorySubmission
        discountInventoryScore
        discountDriverScore
        discountPaperless
        discountBixMutualAffinity
        homeownersPaymentType
        homeownersPaymentMethod
        autoPaymentType
        autoPaymentMethod
        condoPaymentType
        condoPaymentMethod
        currentlyAutoInsured
        homeEffectiveDate
        autoEffectiveDate
        homeSecurityPartnerCustomerType
        employeeDiscount
        autoMegaDownPay
        homeMegaDownPay
        rentersMegaDownPay
        condoMegaDownPay
        ## cv data
        receivedCVData
        hadInforceCVPolicy
        priorIndividualBILimit
        priorOccurrenceBILimit
        priorUmIndividualBodilyInjury
        priorUmOccurrenceBodilyInjury
        priorDrivers {
          firstName
          lastName
        }
        priorCars {
          VIN
          year
          make
          model
          limitRental
          deductibleCollision
          deductibleComprehensive
          roadsideAssistance
        }
        # Farm policy restrictions — surfaced as a banner on the policy screen (#32036 G-06)
        farmVerification {
          restrictions
        }
      }
      rentersCoverage {
        rentersLocation {
          ...Address
        }
        coverageCD
        coverageX
        coverageY
        deductible
      }
      farm {
        mortgageDetails {
          loanNumber
          mortgageHolderName
          mortgageHolderAddress {
            address
            address2
            country
            city
            state
            zip
          }
          mortgageHolderEmail
          mortgageHolderFax
          primary
          isCustom
        }
      }
      condo {
        constructionType
        condoLocation {
          ...Address
        }
        condoQuality
        mortgageDetails {
          loanNumber
          mortgageHolderName
          mortgageHolderAddress {
            address
            address2
            country
            city
            state
            zip
          }
          mortgageHolderEmail
          mortgageHolderFax
          primary
          isCustom
        }
        numFullBathrooms
        numHalfBathrooms
        numOccupants
        ownershipStatus
        purchaseDate
        purchaseAmount
        replacementCostEstimate {
          total
          itemized {
            calculatedValue
            type
            desc
          }
          valuationId
        }
        roofShape
        sqFt
        typeOfCondo
        typeOfEntry
        hasBasement
        buildingHasMoreThanFiveUnits
        yearBuilt
        latitude
        longitude
        fpc
        county
        fireHydrantWithin1000ft
        windstormMitigationLevel
        fpcFromLocation
        floor
        weeksRentedOut
        hasUnitNumber
        numVehicles
        dtoName
        dtbowName
        dtbowLabel
        wildfireHazardScore
        wildfireGallagherScore
      }
      cars {
        addDate
        VIN
        year
        make
        model
        bodyType
        primaryUse
        purchaseDate
        garageLocation {
          ...Address
        }
        isGarageAddressRuleExceptionApproved
        deductibleCollision
        deductibleComprehensive
        limitUMPD
        limitRental
        limitACPE
        limitIncomeLoss
        roadsideAssistance
        extendedNonOwnedCoverage
        solelyOwnedByApplicant
        jointOwner
        rideSharing
        umuimRejected
        confirmed
        aebStatus
        aebConfirmed
        antiTheftStatus
        antiTheftConfirmed
        blindSpotStatus
        blindSpotConfirmed
        singleAirbagStatus
        singleAirbagConfirmed
        doubleAirbagStatus
        doubleAirbagConfirmed
        coverageLoan
        assignedDriver
        userAssignedDriver
        symbolMake
        symbolModel
        symbolStyle
        symbolAux
        annualMilesDriven
        hasOutstandingRecall
        brandedTitle
        allowBrandedTitleCoverage
        allowBrandedTitleCompColl
      }
      drivers {
        ...People
      }
      nonDrivers {
        ...People
      }
      people {
        ...People
      }
      removedDrivers {
        ...People
      }
      trailers {
        VIN
        type
        primaryUse
        year
        value
        contents
        purchaseDate
        garageLocation {
          ...Address
        }
        deductibleCollision
        deductibleComprehensive
        confirmed
        coverageLoan
        addDate
      }
      auto {
        householdMembers
        hasSetPip
        pipResidentsConfirmation
        pipEligibilityConfirmation
        pipResidentsWithQHC
        pipExcludedResidents
        pipAllResidents
        pipEveryoneOnSamePlan
        pipHealthInsuranceType
        pipAdditionalResidents
        residenceInsuranceType
      }
      home {
        basementSqFt
        basementType
        constructionType
        exteriorWallType
        garageCarCapacity
        garageType
        homeLocation {
          ...Address
        }
        homeQuality
        mortgageDetails {
          loanNumber
          mortgageHolderAddress {
            address
            address2
            country
            city
            state
            zip
          }
          mortgageHolderName
          primary
          mortgageHolderEmail
          mortgageHolderFax
          isCustom
        }
        numFamiliesInDwelling
        numFireplaces
        numFullBathrooms
        numHalfBathrooms
        numOccupants
        numStories
        numWoodBurningStoves
        ownershipStatus
        purchaseDate
        purchaseAmount
        replacementCostEstimate {
          total
          itemized {
            calculatedValue
            type
            desc
          }
          valuationId
        }
        roofOriginal
        roofShape
        roofType
        roofYear
        roofDeck
        roofCover
        roofDeckAttachment
        roofToWallConnection
        openingProtection
        roofSWR
        sqFt
        originalSqFt
        swimmingPool
        typeOfHome
        yearBuilt
        latitude
        longitude
        county
        fireHydrantWithin1000ft
        porchCode
        porchSqFt
        hasTrampoline
        constructionStyle
        areasUnderConstruction
        fpc
        fpcFromLocation
        wildfireHazardScore
        wildfireGallagherScore
        dtoName
        dtbowName
        dtbowLabel
        windstormMitigationLevel
        stormShutters
        fortifiedHome
      }
      connectedHome {
        monitored
        moistureDevices
        theftPreventionDevices
        motionDetectingDevices
        smokeDetectingDevices
        autoWaterShutoffDevices
        providerName
        agreeToTerms
      }
      multiPolicyDiscount
      underwritingChanges
      homeViolationDates
      homeSingleViolationDates
      homeClaimDates
    }
  }
  ${Address}
  ${People}
  ${OFFER_FRAGMENT}
  ${FARM_LOCATIONS_FRAGMENT}
  ${FARM_LIABILITY_COVERAGE_FRAGMENT}
`;

export const PolicyPreviewResponse = gql`
  fragment PolicyPreviewResponse on PolicyPreview {
    id
    changeDetails {
      effectiveDate
      updatedDateTime
      targetSegmentId
    }
    coverageChange
    itemsChanged
    itemsChangedAddendum
    billingDescription
    needsUnderwritingReview
    parentPreviewId
    originalPreviewId
    newPremium
    newTotalPrice
    originalTotalPrice
    internalNote
    uwAnswers {
      ...FarmVerification
    }
    clarionDoorIds {
      requestIdBeforePreview
      requestIdAfterPreview
    }
    feesChanged
    nextPayments {
      date
      remainingMonthly
      transactionFee
      total
    }
    diff
    skipImmediateBillOrRefund
    warning
    trailingDocChanges {
      timeStamp
      newValue
      oldValue
      addedChangeKey
      agent
      documentType
      newValueUM
      oldValueUM
      newValueUIM
      oldValueUIM
    }
    createdBy {
      username
      isAgency
    }
    policy {
      ...PolicyFragment
    }
  }
  ${PolicyFragment}
  ${FARM_VERIFICATION_FRAGMENT}
`;
